import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MAX_ZOOM,
  MIN_ZOOM,
  computeCenteredViewport,
} from "./hooks/useCanvasViewportControl";
import {
  StoryboardSavedHtml,
  useSupabaseStore,
} from "@/store/zustand/supabaseStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { decompressHtml } from "@/utils/navTreeCompression";
import type { Storyboard as StoryboardType } from "@/utils/types";
import { clamp, mapValues } from "lodash";
import { useInitialRenderEffect } from "@/utils/utils";

const TRACKPAD_ZOOM_SENSITIVITY = 0.012;
const MOUSE_WHEEL_ZOOM_SENSITIVITY = 0.001;
const META_ZOOM_MULTIPLIER = 0.6;
const PAN_SPEED = 1.5;

type CanvasItem = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
};

const isTrackpadEvent = (event: WheelEvent) => {
  const absDeltaY = Math.abs(event.deltaY);

  if (absDeltaY > 80) {
    return false;
  }

  if (absDeltaY < 15) {
    return true;
  }

  const decimalPart = absDeltaY - Math.floor(absDeltaY);
  const isSpecificMouseFraction =
    Math.abs(decimalPart - 0.000244140625) < 0.000001;

  if (isSpecificMouseFraction) {
    return false;
  }

  return decimalPart > 0;
};

/*
 * This is a read-only pannable zoomable canvas that mimics SimpleCanvas but is
 * re-implemented to avoid any unnecessary performance overhead / coupling.
 * TODO: Use images of storyboards instead of saved HTML when possible to avoid
 * the iframe flash of loading and having to load images.
 */
const SimpleCanvasPreview = <T extends CanvasItem>({
  items,
  renderItem,
}: {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const transformLayerRef = useRef<HTMLDivElement | null>(null);
  const transformRef = useRef({ x: 0, y: 0, zoom: 1 });
  const needsTransformUpdateRef = useRef(true);
  const rafIdRef = useRef<number>();
  const pointerStateRef = useRef({
    id: null as number | null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });
  const hasUserInteractedRef = useRef(false);
  const [isPanning, setIsPanning] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const applyTransform = useCallback(() => {
    rafIdRef.current = undefined;
    if (!needsTransformUpdateRef.current) {
      return;
    }

    const layer = transformLayerRef.current;
    if (!layer) {
      return;
    }

    const { x, y, zoom } = transformRef.current;
    layer.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${zoom})`;
    needsTransformUpdateRef.current = false;
  }, []);

  const scheduleTransform = useCallback(() => {
    if (rafIdRef.current != null) {
      return;
    }
    rafIdRef.current = requestAnimationFrame(applyTransform);
  }, [applyTransform]);

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();

      hasUserInteractedRef.current = true;

      const transform = transformRef.current;
      const container = containerRef.current;
      if (!container) {
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        const sensitivity = isTrackpadEvent(event)
          ? TRACKPAD_ZOOM_SENSITIVITY
          : MOUSE_WHEEL_ZOOM_SENSITIVITY;

        const multiplier = event.metaKey ? META_ZOOM_MULTIPLIER : 1;
        const scaleDelta = Math.exp(-event.deltaY * sensitivity * multiplier);

        const rect = container.getBoundingClientRect();
        const focalX = event.clientX - rect.left - rect.width / 2;
        const focalY = event.clientY - rect.top - rect.height / 2;

        const nextZoom = clamp(transform.zoom * scaleDelta, MIN_ZOOM, MAX_ZOOM);
        const ratio = nextZoom / transform.zoom;

        transform.x = focalX - (focalX - transform.x) * ratio;
        transform.y = focalY - (focalY - transform.y) * ratio;
        transform.zoom = nextZoom;
      } else {
        transform.x -= event.deltaX * PAN_SPEED;
        transform.y -= event.deltaY * PAN_SPEED;
      }

      needsTransformUpdateRef.current = true;
      scheduleTransform();
    },
    [scheduleTransform],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();

      hasUserInteractedRef.current = true;

      const container = containerRef.current;
      if (!container) {
        return;
      }

      container.setPointerCapture(event.pointerId);
      pointerStateRef.current = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: transformRef.current.x,
        originY: transformRef.current.y,
      };
      setIsPanning(true);
    },
    [],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const state = pointerStateRef.current;
      if (state.id !== event.pointerId) {
        return;
      }

      event.preventDefault();

      transformRef.current.x = state.originX + (event.clientX - state.startX);
      transformRef.current.y = state.originY + (event.clientY - state.startY);
      needsTransformUpdateRef.current = true;
      scheduleTransform();
    },
    [scheduleTransform],
  );

  const endPointerPan = useCallback((pointerId: number) => {
    const container = containerRef.current;
    if (container && container.hasPointerCapture(pointerId)) {
      container.releasePointerCapture(pointerId);
    }
    pointerStateRef.current = {
      id: null,
      startX: 0,
      startY: 0,
      originX: transformRef.current.x,
      originY: transformRef.current.y,
    };
    setIsPanning(false);
  }, []);

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointerStateRef.current.id !== event.pointerId) {
        return;
      }
      event.preventDefault();
      endPointerPan(event.pointerId);
    },
    [endPointerPan],
  );

  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointerStateRef.current.id !== event.pointerId) {
        return;
      }
      endPointerPan(event.pointerId);
    },
    [endPointerPan],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel]);

  useEffect(() => {
    needsTransformUpdateRef.current = true;
    scheduleTransform();

    return () => {
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [scheduleTransform]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;
      setContainerSize((prev) => {
        if (prev.width === width && prev.height === height) {
          return prev;
        }
        return { width, height };
      });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const [hasSetInitialViewport, setHasSetInitialViewport] = useState(false);

  useEffect(() => {
    if (
      items.length === 0 ||
      containerSize.width === 0 ||
      containerSize.height === 0
    ) {
      return;
    }

    if (hasUserInteractedRef.current && hasSetInitialViewport) {
      return;
    }

    const viewport = computeCenteredViewport(
      items.map((item) => ({
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
      })),
      containerSize.width,
      containerSize.height,
      0.7,
      null,
    );
    const boundedZoom = clamp(viewport.zoom, MIN_ZOOM, MAX_ZOOM);

    transformRef.current = { x: viewport.x, y: viewport.y, zoom: boundedZoom };
    needsTransformUpdateRef.current = true;
    scheduleTransform();
    setHasSetInitialViewport(true);
  }, [items, containerSize.width, containerSize.height, scheduleTransform]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full touch-none select-none overflow-hidden bg-secondary"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      style={{ cursor: isPanning ? "grabbing" : "auto" }}
    >
      <div
        className="absolute left-1/2 top-1/2"
        style={{ transform: "translate3d(-50%, -50%, 0)" }}
      >
        <div
          ref={transformLayerRef}
          className="relative"
          style={{
            transform: "translate3d(0px, 0px, 0) scale(1)",
            transformOrigin: "0 0",
            willChange: isPanning ? "transform" : "auto",
            opacity: hasSetInitialViewport ? 1 : 0.001,
          }}
        >
          {items.map(renderItem)}
        </div>
      </div>
    </div>
  );
};

/**
 * Storyboard-specific canvas preview component
 */
export const CanvasPreview = ({ canvasId }: { canvasId: string }) => {
  const supabaseClient = useSupabaseStore((store) => store.supabaseClient);
  const fetchSavedStoryboardHtmlForStoryboards = useSupabaseStore(
    (store) => store.fetchSavedStoryboardHtmlForStoryboards,
  );
  const savedStoryboardHtml = useSupabaseStore(
    (store) => store.savedStoryboardHtml,
  );
  const previewStoryboards = useIssuesStore(
    (store) => store.canvasIdToPreviewStoryboards[canvasId],
  );
  const fetchPreviewStoryboardsForCanvas = useIssuesStore(
    (store) => store.fetchPreviewStoryboardsForCanvas,
  );

  const storyboards = (previewStoryboards ?? []).filter(
    (storyboard) => !storyboard.deleted,
  );
  const storyboardHtmls = useMemo(
    () =>
      Object.fromEntries(
        storyboards.map((storyboard: StoryboardType) => {
          const html = savedStoryboardHtml?.[storyboard.id]?.html;
          const decompressedHtml = html ? decompressHtml(html) : null;
          return [storyboard.id, decompressedHtml];
        }),
      ),
    [storyboards, savedStoryboardHtml],
  );

  useInitialRenderEffect(() => {
    // If we don't have preview storyboards yet, try to fetch them
    if (previewStoryboards === undefined) {
      fetchPreviewStoryboardsForCanvas(supabaseClient, canvasId);
    }
    // Fetch the HTML content for any storyboards we have
    if (storyboards.length > 0) {
      fetchSavedStoryboardHtmlForStoryboards(
        storyboards.map((storyboard) => storyboard.id),
      );
    }
  });

  const renderStoryboard = useCallback(
    (storyboard: StoryboardType) => {
      const content = storyboardHtmls?.[storyboard.id] ? (
        <iframe
          srcDoc={storyboardHtmls?.[storyboard.id] ?? ""}
          sandbox="allow-same-origin"
          className="h-full w-full border-0"
          style={{
            pointerEvents: "none",
            backgroundColor: "transparent",
          }}
          title={`Storyboard ${storyboard.id}`}
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center text-md bg-secondary-400 text-muted-foreground"
          style={{ pointerEvents: "none" }}
        >
          No saved preview
        </div>
      );

      return (
        <div
          key={storyboard.id}
          className="absolute overflow-hidden rounded border border-border-primary/40 shadow-xs"
          style={{
            width: storyboard.width,
            height: storyboard.height,
            transform: `translate3d(${storyboard.x}px, ${storyboard.y}px, 0)`,
            transformOrigin: "0 0",
            pointerEvents: "none",
            backgroundColor: "transparent",
          }}
          aria-hidden
        >
          {content}
        </div>
      );
    },
    [storyboardHtmls],
  );

  // Hide preview if no page with previews exists
  if (storyboards.length === 0) {
    return null;
  }

  return (
    <div className="h-[300px] w-full">
      <SimpleCanvasPreview items={storyboards} renderItem={renderStoryboard} />
    </div>
  );
};
